import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { RootState } from "../../../redux/store/store";
import {
  hasAnyPermission,
  hasCustomPermission,
  hasPermission,
  usePermissions,
  Permissions,
} from "../../permissions";
import { ModuleRouteGuardProps } from "./interface";
import CenteredLoader from "../loader/CenteredLoader";

const ModuleRouteGuard: React.FC<ModuleRouteGuardProps> = ({
  children,
  moduleType,
  routePath,
  requiredPermissions,
  redirectTo,
}) => {
  const { isLoadingUserData, user } = useSelector(
    (store: RootState) => store.auth,
  );
  const permissions = usePermissions();

  if (isLoadingUserData || permissions === null) {
    return (
      <div style={{ height: "100vh", width: "100vw" }}>
        <CenteredLoader />
      </div>
    );
  }

  let hasAccess = false;

  if (requiredPermissions) {
    hasAccess = hasAnyPermission(permissions, requiredPermissions, "viewAll");
  } else {
    switch (moduleType) {
      case "admin":
        hasAccess = checkAdminPermissions(permissions, user, routePath);
        break;
      case "ehr":
        hasAccess = checkEHRPermissions(permissions, user, routePath);
        break;
      case "billing":
        hasAccess = checkBillingPermissions(permissions, user, routePath);
        break;
      case "employer-management":
        hasAccess = checkEmployerManagementPermissions(
          permissions,
          user,
          routePath,
        );
        break;
      case "employer-portal":
        hasAccess = checkEmployerPortalPermissions(
          permissions,
          user,
          routePath,
        );
        break;
      case "inventory":
        hasAccess = checkInventoryPermissions(permissions, user, routePath);
        break;
      case "reports":
        // hasAccess = checkReportsPermissions(permissions, user, routePath);
        hasAccess = true;
        break;
      case "crm":
        hasAccess = checkCRMPermissions(permissions, user, routePath);
        break;
      case "affiliate-portal":
        hasAccess = checkAffiliatePortalPermissions(
          permissions,
          user,
          routePath,
        );
        break;
      default:
        hasAccess = true;
    }
  }

  if (!hasAccess) {
    const redirectPath = redirectTo || `/${moduleType}/dashboard`;
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

const checkAdminPermissions = (
  permissions: Permissions | null,
  user: any,
  routePath?: string,
) => {
  const hasDashboardPermission = hasPermission(
    permissions,
    "Admin Dashboard",
    "viewAll",
  );
  const hasMembershipPermission = hasPermission(
    permissions,
    "Admin Membership Plan",
    "viewAll",
  );
  const hasAffiliatePermission = hasPermission(
    permissions,
    "Admin Affiliate",
    "viewAll",
  );
  const hasSettingsPermission = hasPermission(
    permissions,
    "Admin Settings",
    "viewAll",
  );
  const hasReportsPermission = hasPermission(
    permissions,
    "Admin Reports",
    "viewAll",
  );
  const hasFormBuilderPermission = hasPermission(
    permissions,
    "Admin Form Builder",
    "viewAll",
  );
  const hasEducationMaterialsPermission = hasPermission(
    permissions,
    "Admin Educational Material",
    "viewAll",
  );
  const hasUsersPermission = hasPermission(
    permissions,
    "Admin Account User",
    "viewAll",
  );
  const hasPaymentSourcePermission = hasPermission(
    permissions,
    "Admin Account Payment source",
    "viewAll",
  );
  const hasRolesPermissionsPermission = hasPermission(
    permissions,
    "Admin Roles and Permissions",
    "viewAll",
  );
  const hasNotificationsPermission = hasPermission(
    permissions,
    "Admin Notifications",
    "viewAll",
  );
  const hasChargeAndTaxesPermission = hasPermission(
    permissions,
    "Admin Charge and Taxes",
    "viewAll",
  );
  const hasProvidersPermission = hasPermission(
    permissions,
    "Admin Providers",
    "viewAll",
  );
  const hasLocationsPermission = hasPermission(
    permissions,
    "Admin Locations",
    "viewAll",
  );
  const hasLocationGroupsPermission = hasPermission(
    permissions,
    "Admin Location Groups",
    "viewAll",
  );
  const hasCouponsPermission = hasPermission(
    permissions,
    "Admin Coupons - Retail patient",
    "viewAll",
  );
  const hasTermsConditionsPermission = hasPermission(
    permissions,
    "Admin Terms and conditions",
    "viewAll",
  );
  const hasAuditLogPermission = hasPermission(
    permissions,
    "Admin Audit Log",
    "viewAll",
  );
  const hasCancellationReasonPermission = hasPermission(
    permissions,
    "Admin Cancellation Reason",
    "viewAll",
  );
  const hasInvoicingAheadPermission = hasPermission(
    permissions,
    "Admin Invoicing Ahead of Time",
    "viewAll",
  );

  switch (routePath) {
    case "dashboard":
      return hasDashboardPermission;
    case "users":
      return hasUsersPermission;
    case "form-builder":
      return hasFormBuilderPermission;
    case "reports":
      return hasReportsPermission;
    case "education-materials":
      return hasEducationMaterialsPermission;
    case "membership":
      return hasMembershipPermission;
    case "affiliate":
      return hasAffiliatePermission;
    case "settings":
      return hasSettingsPermission;
    case "payment-sources":
      return hasPaymentSourcePermission;
    case "roles-and-permissions":
      return hasRolesPermissionsPermission;
    case "notifications":
      return hasNotificationsPermission;
    case "charge-and-taxes":
      return hasChargeAndTaxesPermission;
    case "providers":
      return hasProvidersPermission;
    case "locations":
      return hasLocationsPermission;
    case "location-groups":
      return hasLocationGroupsPermission;
    case "coupons":
      return hasCouponsPermission;
    case "terms-and-conditions":
      return hasTermsConditionsPermission;
    case "audit-log":
      return hasAuditLogPermission;
    case "global-holidays":
      return hasSettingsPermission;
    case "cancellation-reason":
      return hasCancellationReasonPermission;
    case "invoicing-ahead":
      return hasInvoicingAheadPermission;
    default:
      return hasDashboardPermission;
  }
};

const checkEHRPermissions = (
  permissions: Permissions | null,
  user: any,
  routePath?: string,
) => {
  const hasDashboardPermission = hasPermission(
    permissions,
    "EHR Dashboard",
    "viewAll",
  );
  const hasPatientsPermission = hasPermission(
    permissions,
    "EHR Patient Dashboard",
    "viewAll",
  );
  const hasSchedulingPermission = hasPermission(
    permissions,
    "EHR Scheduling",
    "viewAll",
  );
  const hasTasksPermission = hasPermission(permissions, "EHR Tasks", "viewAll");
  const hasReferralPermission = hasPermission(
    permissions,
    "EHR Referral",
    "viewAll",
  );
  const hasSettingsPermission = hasPermission(
    permissions,
    "EHR CPOE",
    "viewAll",
  );
  const hasPatientProfilePermission = hasPermission(
    permissions,
    "EHR Patient Profile",
    "viewAll",
  );
  const hasPatientDashboardPermission = hasPermission(
    permissions,
    "EHR Patient Dashboard",
    "viewAll",
  );
  const hasPatientEducationPermission = hasPermission(
    permissions,
    "EHR Patient Educational Material",
    "viewAll",
  );
  const hasPatientAllergiesPermission = hasPermission(
    permissions,
    "EHR Patient allergies",
    "viewAll",
  );
  const hasPatientMedicationsPermission = hasPermission(
    permissions,
    "EHR Patient Medications",
    "viewAll",
  );
  const hasPatientDiagnosisPermission = hasPermission(
    permissions,
    "EHR Patient Diagnosis",
    "viewAll",
  );
  const hasPatientVaccinesPermission = hasPermission(
    permissions,
    "EHR Patient Vaccines",
    "viewAll",
  );
  const hasPatientImagingPermission = hasPermission(
    permissions,
    "EHR Patient Imaging",
    "viewAll",
  );
  const hasPatientLabsPermission = hasPermission(
    permissions,
    "EHR Patient Labs",
    "viewAll",
  );
  const hasPatientReferralsPermission = hasPermission(
    permissions,
    "EHR Patient Referrals",
    "viewAll",
  );
  const hasPatientDocumentsPermission = hasPermission(
    permissions,
    "EHR Patient Documents",
    "viewAll",
  );
  const hasPatientMessagesPermission = hasPermission(
    permissions,
    "EHR Patient Messages",
    "viewAll",
  );
  const hasPatientTasksPermission = hasPermission(
    permissions,
    "EHR Patient Tasks",
    "viewAll",
  );
  const hasPatientHistoryPermission = hasPermission(
    permissions,
    "EHR Patient History",
    "viewAll",
  );
  const hasPatientVisitDetailsPermission = hasPermission(
    permissions,
    "EHR Patient Visit Details",
    "viewAll",
  );
  const hasPatientTimelinePermission = hasPermission(
    permissions,
    "EHR Patient timeline",
    "viewAll",
  );
  const hasPatientDrugTolerancePermission = hasPermission(
    permissions,
    "EHR Patient Drug Tolerance",
    "viewAll",
  );
  const hasPatientPharmaciesPermission = hasPermission(
    permissions,
    "EHR Patient Pharmacies",
    "viewAll",
  );
  const hasPatientInvoicePermission = hasPermission(
    permissions,
    "EHR Patient Invoice",
    "viewAll",
  );
  const hasPatientMembershipPermission = hasPermission(
    permissions,
    "EHR Patient Membership Details",
    "viewAll",
  );
  const hasAppointmentTypePermission = hasPermission(
    permissions,
    "EHR Appointment type",
    "viewAll",
  );
  const hasAvailabilityPermission = hasPermission(
    permissions,
    "EHR Availabillity",
    "viewAll",
  );
  const hasStatusConfigPermission = hasPermission(
    permissions,
    "EHR Status Configuration",
    "viewAll",
  );
  const hasCarePlansPermission = hasPermission(
    permissions,
    "EHR Care Plan",
    "viewAll",
  );

  const hasCustomizableInfoPermission =
    user?.permission?.["EHR Customizable Information Sharing"]?.viewAll;

  switch (routePath) {
    case "dashboard":
      return hasDashboardPermission;
    case "patients":
      return hasPatientsPermission;
    case "scheduling":
      return hasSchedulingPermission;
    case "tasks":
      return hasTasksPermission;
    case "referral":
      return hasReferralPermission;
    case "settings":
      return hasCustomPermission(permissions, "EHR_Settings_Custom", user);
    case "care-plans":
      return hasCarePlansPermission;
    case "cpoe":
      return hasSettingsPermission;
    case "patient-profile":
      return hasPatientProfilePermission;
    case "patient-dashboard":
      return hasPatientDashboardPermission;
    case "patient-education":
      return hasPatientEducationPermission;
    case "patient-allergies":
      return hasPatientAllergiesPermission;
    case "patient-medications":
      return hasPatientMedicationsPermission;
    case "patient-diagnosis":
      return hasPatientDiagnosisPermission;
    case "patient-vaccines":
      return hasPatientVaccinesPermission;
    case "patient-imaging":
      return hasPatientImagingPermission;
    case "patient-labs":
      return hasPatientLabsPermission;
    case "patient-referrals":
      return hasPatientReferralsPermission;
    case "patient-documents":
      return hasPatientDocumentsPermission;
    case "patient-messages":
      return hasPatientMessagesPermission;
    case "patient-tasks":
      return hasPatientTasksPermission;
    case "patient-history":
      return hasPatientHistoryPermission;
    case "patient-visit-details":
      return hasPatientVisitDetailsPermission;
    case "patient-timeline":
      return hasPatientTimelinePermission;
    case "patient-drug-tolerance":
      return hasPatientDrugTolerancePermission;
    case "patient-pharmacies":
      return hasPatientPharmaciesPermission;
    case "patient-invoice":
      return hasPatientInvoicePermission;
    case "patient-membership":
      return hasPatientMembershipPermission;
    case "appointment-type":
      return hasAppointmentTypePermission;
    case "availability":
      return hasAvailabilityPermission;
    case "status-configuration":
      return hasStatusConfigPermission;
    case "customizable-information-sharing":
      return hasCustomizableInfoPermission;
    case "provider-directory":
      return (
        hasPermission(permissions, "Provider Directory", "view") ||
        hasPermission(permissions, "Provider Directory", "viewAll")
      );
    default:
      return hasDashboardPermission;
  }
};

const checkBillingPermissions = (
  permissions: Permissions | null,
  user: any,
  routePath?: string,
) => {
  const hasDashboardPermission = hasPermission(
    permissions,
    "Billing",
    "viewAll",
  );
  const hasPatientsPermission = hasPermission(
    permissions,
    "Billing Patients",
    "viewAll",
  );
  const hasEmployersPermission = hasPermission(
    permissions,
    "Billing Employers",
    "viewAll",
  );
  const hasBasicDetailsPermission = hasPermission(
    permissions,
    "Billing Basic Details",
    "viewAll",
  );
  const hasEmployeeInvoicesPermission = hasPermission(
    permissions,
    "Billing Employee Invoices",
    "viewAll",
  );
  const hasCreditsPermission = hasPermission(
    permissions,
    "Billing Credits",
    "viewAll",
  );
  const hasMembershipsPermission = hasPermission(
    permissions,
    "Billing Memberships",
    "viewAll",
  );
  const hasEmployeePaymentOptionPermission = hasPermission(
    permissions,
    "Billing Employee Payment Option",
    "viewAll",
  );
  const hasEmployeeNotesPermission = hasPermission(
    permissions,
    "Billing employee Notes",
    "viewAll",
  );
  const hasEmployeeNotificationsPermission = hasPermission(
    permissions,
    "Billing employee Notifications",
    "viewAll",
  );
  const hasInvoicesPermission = hasPermission(
    permissions,
    "Billing Invoices",
    "viewAll",
  );
  const hasSettingsPermission = hasPermission(
    permissions,
    "Billing Basic Details",
    "viewAll",
  );
  const hasInvoiceSettingsPermission = hasPermission(
    permissions,
    "Billing Invoice Settings",
    "viewAll",
  );
  const hasPlansPermission = hasPermission(
    permissions,
    "Billing Plans",
    "viewAll",
  );
  const hasPricingPermission = hasPermission(
    permissions,
    "Billing Pricing",
    "viewAll",
  );
  const hasNotificationsPermission = hasPermission(
    permissions,
    "Billing Notifications",
    "viewAll",
  );
  const hasNotesPermission = hasPermission(
    permissions,
    "Billing Employer Notes",
    "viewAll",
  );
  const hasBrokersPermission = hasPermission(
    permissions,
    "Billing Brokers",
    "viewAll",
  );
  const hasRetailPermission = hasPermission(
    permissions,
    "Billing Retail",
    "viewAll",
  );
  const hasEmployerPaymentOptionPermission = hasPermission(
    permissions,
    "Billing Employer Payment Option",
    "viewAll",
  );
  switch (routePath) {
    // case "dashboard":
    //   return hasDashboardPermission;
    case "all-employees":
      return hasPatientsPermission;
    case "employers":
      return hasEmployersPermission;
    case "settings":
      return hasSettingsPermission;
    case "profile":
      return hasSettingsPermission;
    case "basic-details":
      return hasBasicDetailsPermission;
    case "employee-invoices":
      return hasEmployeeInvoicesPermission;
    case "credits":
      return hasCreditsPermission;
    case "memberships":
      return hasMembershipsPermission;

    case "employee-notes":
      return hasEmployeeNotesPermission;
    case "employee-notifications":
      return hasEmployeeNotificationsPermission;
    case "invoices":
      return hasInvoicesPermission;
    case "invoice-settings":
      return hasInvoiceSettingsPermission;

    case "plans":
      return hasPlansPermission;
    case "pricing":
      return hasPricingPermission;
    case "notifications":
      return hasNotificationsPermission;
    case "notes":
      return hasNotesPermission;
    case "brokers":
      return hasBrokersPermission;
    case "retail":
      return hasRetailPermission;
    case "employer-payment-option":
      return hasEmployerPaymentOptionPermission;
    case "employee-payment-option":
      return hasEmployeePaymentOptionPermission;
    default:
      return hasDashboardPermission;
  }
};

const checkEmployerManagementPermissions = (
  permissions: Permissions | null,
  user: any,
  routePath?: string,
) => {
  const hasDashboardPermission = hasPermission(
    permissions,
    "EM Dashboard",
    "viewAll",
  );
  const hasEmployersPermission = hasPermission(
    permissions,
    "EM Employers",
    "viewAll",
  );
  const hasEmployeesViewAll = hasPermission(
    permissions,
    "EM All employees",
    "viewAll",
  );
  const hasEmployeesView = hasPermission(
    permissions,
    "EM All employees",
    "view",
  );
  const hasEmployeesPermission = hasEmployeesViewAll || hasEmployeesView;
  const hasBasicDetailsPermission = hasPermission(
    permissions,
    "EM Basic Details",
    "viewAll",
  );
  const hasInvoicesPermission = hasPermission(
    permissions,
    "EM Invoices",
    "viewAll",
  );
  const hasCreditsPermission = hasPermission(
    permissions,
    "EM Credits",
    "viewAll",
  );
  const hasMembershipsPermission = hasPermission(
    permissions,
    "EM Memberships",
    "viewAll",
  );
  const hasEmployeePaymentOptionPermission = hasPermission(
    permissions,
    "EM Employee Payment Option",
    "viewAll",
  );
  const hasNotesPermission = hasPermission(permissions, "EM Notes", "viewAll");
  const hasEmployeeNotificationsPermission = hasPermission(
    permissions,
    "EM Employee Notifications",
    "viewAll",
  );
  const hasSettingsPermission = hasPermission(
    permissions,
    "EM Basic Details",
    "viewAll",
  );
  const hasAccountDetailsPermission = hasPermission(
    permissions,
    "EM Account Details",
    "viewAll",
  );
  const hasInvoiceSettingsPermission = hasPermission(
    permissions,
    "EM Invoice Settings",
    "viewAll",
  );
  const hasPaymentOptionPermission = hasPermission(
    permissions,
    "EM Employer Payment Option",
    "viewAll",
  );
  const hasPlansPermission = hasPermission(permissions, "EM Plans", "viewAll");
  const hasPricingPermission = hasPermission(
    permissions,
    "EM Pricing",
    "viewAll",
  );
  const hasNotificationsPermission = hasPermission(
    permissions,
    "EM Notifications",
    "viewAll",
  );
  const hasBrokersViewAll = hasPermission(permissions, "EM Brokers", "viewAll");
  const hasBrokersView = hasPermission(permissions, "EM Brokers", "view");
  const hasBrokersPermission = hasBrokersViewAll || hasBrokersView;
  const hasEmployeeUploadsPermission = hasPermission(
    permissions,
    "EM Employee Uploads",
    "viewAll",
  );
  const hasOnlineSignupPermission = hasPermission(
    permissions,
    "EM Online Signup",
    "viewAll",
  );

  switch (routePath) {
    case "dashboard":
      return hasDashboardPermission;
    case "employers":
      return hasEmployersPermission;
    case "all-employees":
      return hasEmployeesPermission;
    case "basic-details":
      return hasBasicDetailsPermission;
    case "invoices":
      return hasInvoicesPermission;
    case "credits":
      return hasCreditsPermission;
    case "memberships":
      return hasMembershipsPermission;
    case "notes":
      return hasNotesPermission;
    case "employee-notifications":
      return hasEmployeeNotificationsPermission;
    case "settings":
      return hasSettingsPermission;
    case "account-details":
      return hasAccountDetailsPermission;
    case "invoice-settings":
      return hasInvoiceSettingsPermission;
    case "employer-payment-option":
      return hasPaymentOptionPermission;
    case "plans":
      return hasPlansPermission;
    case "pricing":
      return hasPricingPermission;
    case "notifications":
      return hasNotificationsPermission;
    case "brokers":
      return hasBrokersPermission;
    case "brokers-firm":
      return hasBrokersPermission; //IMP-->Change this permission to firm
    case "employee-uploads":
      return hasEmployeeUploadsPermission;
    case "online-signup":
      return hasOnlineSignupPermission;
    case "employees":
      return hasEmployeesPermission;
    case "coverage-plans":
      return hasPlansPermission;
    case "profile":
      return hasSettingsPermission;
    default:
      return hasDashboardPermission;
  }
};

const checkEmployerPortalPermissions = (
  permissions: Permissions | null,
  user: any,
  routePath?: string,
) => {
  const hasDashboardPermission = hasPermission(
    permissions,
    "EMP Dashboard",
    "viewAll",
  );
  const hasAllEmployeesPermission = hasPermission(
    permissions,
    "EMP All employees",
    "viewAll",
  );
  const hasMembershipsPermission = hasPermission(
    permissions,
    "EMP Memberships",
    "viewAll",
  );
  const hasEmployeeMembershipsPermission = hasPermission(
    permissions,
    "EMP Employee Memberships",
    "viewAll",
  );
  const hasNotesPermission = hasPermission(permissions, "EMP Notes", "viewAll");
  const hasNotificationsPermission = hasPermission(
    permissions,
    "EMP Notifications",
    "viewAll",
  );
  const hasInvoicesPermission = hasPermission(
    permissions,
    "EMP Invoices",
    "viewAll",
  );
  const hasPaymentOptionPermission = hasPermission(
    permissions,
    "EMP Payment Option",
    "viewAll",
  );
  const hasEmployeeNotificationsPermission = hasPermission(
    permissions,
    "EMP Employee Notifications",
    "viewAll",
  );
  const hasEmployeeUploadsPermission = hasPermission(
    permissions,
    "EMP Employee Uploads",
    "viewAll",
  );
  const hasSettingsPermission = hasPermission(
    permissions,
    "EMP Basic Details",
    "viewAll",
  );
  const hasCoveragePlansPermission = hasPermission(
    permissions,
    "EMP Coverage Plan",
    "viewAll",
  );

  const hasEmployerSettingsPermission = true;

  const hasEmployerNotificationPerferancePermission = hasPermission(
    permissions,
    "EMP Notification Preferences",
    "viewAll",
  );

  const hasAdminListPermission = hasPermission(
    permissions,
    "EMP Admin List",
    "viewAll",
  );

  switch (routePath) {
    case "dashboard":
      return hasDashboardPermission;
    case "all-employees":
      return hasAllEmployeesPermission;
    case "memberships":
      return hasMembershipsPermission;
    case "employee-memberships":
      return hasEmployeeMembershipsPermission;
    case "notes":
      return hasNotesPermission;
    case "notifications":
      return hasNotificationsPermission;
    case "invoices":
      return hasInvoicesPermission;
    case "payment-option":
      return hasPaymentOptionPermission;
    case "employee-notifications":
      return hasEmployeeNotificationsPermission;
    case "employee-uploads":
      return hasEmployeeUploadsPermission;
    case "settings":
      return hasSettingsPermission;
    case "coverage-plans":
      return hasCoveragePlansPermission;
    case "emp-settings":
      return hasEmployerSettingsPermission;
    case "emp-notification":
      return hasEmployerNotificationPerferancePermission;
    case "tickets":
      return hasNotesPermission;
    case "adminlist":
      return hasAdminListPermission;
    default:
      return hasDashboardPermission;
  }
};

const checkInventoryPermissions = (
  permissions: Permissions | null,
  user: any,
  routePath?: string,
) => {
  const canView = (featureName: string) =>
    hasPermission(permissions, featureName, "viewAll");

  switch (routePath) {
    case "":
    case "dashboard":
      return canView("Inventory Dashboard");
    case "formulary":
    case "add-medications":
      return canView("Inventory Formulary");
    case "shipments":
      return canView("Inventory Shipment");
    case "inventory":
      return canView("Inventory Inventory");
    case "orders":
    case "cart":
      return canView("Inventory Orders");
    case "reports":
      return canView("Inventory Reports");
    case "settings":
      return canView("Inventory Settings");
    default:
      return hasPermission(permissions, "Inventory", "viewAll");
  }
};

const checkReportsPermissions = (
  permissions: Permissions | null,
  user: any,
  routePath?: string,
) => {
  const hasClinicActivityPermission = hasPermission(
    permissions,
    "Clinic/Provider Activity",
    "viewAll",
  );
  const hasHealthTrendsPermission = hasPermission(
    permissions,
    "Health Trends/Diagnosis",
    "viewAll",
  );
  const hasUtilizationPermission = hasPermission(
    permissions,
    "Utilization/Engagement",
    "viewAll",
  );
  const hasChurnReportsPermission = hasPermission(
    permissions,
    "Churn Reports",
    "viewAll",
  );
  const hasInvoicesReportPermission = hasPermission(
    permissions,
    "Invoices Report",
    "viewAll",
  );
  const hasSavingsReportPermission = hasPermission(
    permissions,
    "Savings Report",
    "viewAll",
  );

  switch (routePath) {
    case "":
    case "dashboard":
      return (
        hasClinicActivityPermission ||
        hasHealthTrendsPermission ||
        hasUtilizationPermission
      );
    case "clinic-activity":
      return hasClinicActivityPermission;
    case "health-trends":
      return hasHealthTrendsPermission;
    case "utilization":
      return hasUtilizationPermission;
    case "churn":
      return hasChurnReportsPermission;
    case "invoices":
      return hasInvoicesReportPermission;
    case "savings":
      return hasSavingsReportPermission;
    default:
      return (
        hasClinicActivityPermission ||
        hasHealthTrendsPermission ||
        hasUtilizationPermission
      );
  }
};

const checkCRMPermissions = (
  permissions: Permissions | null,
  user: any,
  routePath?: string,
) => {
  const hasRetailPermission = hasPermission(
    permissions,
    "CRM - Retail",
    "viewAll",
  );
  const hasEmployerPermission = hasPermission(
    permissions,
    "CRM - Employer",
    "viewAll",
  );
  const hasLeadSourcesPermission = hasPermission(
    permissions,
    "Lead Sources",
    "viewAll",
  );

  switch (routePath) {
    case "crm":
    case "leads":
      return hasRetailPermission || hasEmployerPermission;
    case "membership-plans":
      return hasRetailPermission || hasEmployerPermission;
    case "notifications":
      return hasRetailPermission || hasEmployerPermission;
    case "lead-sources":
      return hasLeadSourcesPermission;
    default:
      return hasRetailPermission || hasEmployerPermission;
  }
};

const checkAffiliatePortalPermissions = (
  permissions: Permissions | null,
  user: any,
  routePath?: string,
) => {
  const hasDashboardPermission = hasPermission(
    permissions,
    "Admin Affiliate Dashboard",
    "viewAll",
  );
  const hasProvidersPermission = hasPermission(
    permissions,
    "Admin Affiliate Linked Providers and Locations Provider",
    "viewAll",
  );
  const hasLocationsPermission = hasPermission(
    permissions,
    "Admin Affiliate Linked Providers and Locations Locations",
    "viewAll",
  );
  const hasLocationGroupsPermission = hasPermission(
    permissions,
    "Affiliate Location Groups",
    "viewAll",
  );
  const hasMapsPermission = hasPermission(
    permissions,
    "Affiliate Maps",
    "viewAll",
  );
  const hasChargeCategoriesPermission = hasPermission(
    permissions,
    "Affiliate Charge Categories",
    "viewAll",
  );
  const hasSettingsPermission = hasPermission(
    permissions,
    "Admin Affiliate Settings",
    "viewAll",
  );
  const hasSettingsProvidersPermission = hasPermission(
    permissions,
    "Affiliate Settings Providers",
    "viewAll",
  );
  const hasSettingsNotificationPreferencePermission = hasPermission(
    permissions,
    "Admin Affiliate Notification Preference",
    "viewAll",
  );
  const hasSettingsTermsPermission = hasPermission(
    permissions,
    "Affiliate Settings Terms",
    "viewAll",
  );
  const hasSettingsProfilePermission = hasPermission(
    permissions,
    "Affiliate Settings Profile",
    "viewAll",
  );
  const hasBillsPermission = hasPermission(
    permissions,
    "Admin Affiliate Bills",
    "viewAll",
  );
  const hasPatientsPermission = hasPermission(
    permissions,
    "Admin Affiliate Patients",
    "viewAll",
  );
  const hasLinkedProvidersPermission = hasPermission(
    permissions,
    "Affiliate Linked Providers",
    "viewAll",
  );
  const hasLinkedLocationsPermission = hasPermission(
    permissions,
    "Affiliate Linked Locations",
    "viewAll",
  );

  switch (routePath) {
    case "dashboard":
      return hasDashboardPermission;
    case "providers":
      return hasProvidersPermission;
    case "locations":
      return hasLocationsPermission;
    case "location-groups":
      return hasLocationGroupsPermission;
    case "maps":
      return hasMapsPermission;
    case "charge-categories":
      return hasChargeCategoriesPermission;
    case "settings":
      return hasSettingsPermission;
    case "settings-providers":
      return hasSettingsProvidersPermission;
    case "settings-notification-preference":
      return hasSettingsNotificationPreferencePermission;
    case "settings-terms":
      return hasSettingsTermsPermission;
    case "settings-profile":
      return hasSettingsProfilePermission;
    case "bills":
      return hasBillsPermission;
    case "patients":
      return hasPatientsPermission;
    case "linked-providers":
      return hasLinkedProvidersPermission;
    case "linked-locations":
      return hasLinkedLocationsPermission;
    default:
      return hasDashboardPermission;
  }
};

export default ModuleRouteGuard;
